/* -disable prefer-destructuring */
// NPM imports
import { createSelector } from 'reselect';
import moment from 'moment';

// project imports
import { detailsRanges as dashboarCompletedDetailsRanges } from './dashboard-completed';

import { getMeasurementUnitFieldName } from '../components/MeasurementValue';
import { performanceStatus } from '../pages/Tracking/utils/delivery-status';

// Base selectors
const authStateSelector = state => state.auth;
const suggestionSelector = state => state.suggestion;
const dashboarCompletedStateSelector = state => state.dashboardCompleted;
const dashboardHomeStateSelector = state => state.dashboardHome;
const dashboardForecastStateSelector = state => state.dashboardForecast;
const dashboardRealTimeStateSelector = state => state.dashboardRealTime;
const trackingSelector = state => state.tracking;
const deliveryStatusSelector = state => state.deliveryStatus;
const platformUsersSelector = state => state.adminPlatformUsers;
const companiesSelector = state => state.adminCompanies;
const luftUsersSelector = state => state.adminLuftUsers;
const stockStateSelector = state => state.stock;
const schedulingSelector = state => state.scheduling;
const newScheduleSelector = state => state.newSchedule;
const notificationSettingsSelector = state => state.adminNotificationSettings;

// Util
const extractRequestUI = obj => ({
  isFetching: obj.isFetching,
  requested: obj.requested || null,
  received: obj.received || null,
  error: obj.error,
});

const extractMultipleRequestUI = (objArr = []) => ({
  isFetching: objArr.find(obj => obj.isFetching),
  requested: moment
    .max(objArr.filter(obj => obj.requested).map(obj => moment(obj.requested)))
    .format(),
  received: moment
    .max(objArr.filter(obj => !!obj.received).map(obj => moment(obj.received)))
    .format(),
  error: objArr.reduce((errorMsg, obj) => {
    const objError = obj.error;
    return objError
      ? errorMsg
        ? `${errorMsg}. ${objError}`
        : objError
      : errorMsg;
  }, ''),
});

// Auth
export const getLoginUI = createSelector(authStateSelector, authState =>
  extractRequestUI(authState.login),
);

export const getForgotPasswordUI = createSelector(
  authStateSelector,
  authState => extractRequestUI(authState.forgotPassword),
);

export const getRecoverPasswordUI = createSelector(
  authStateSelector,
  authState => extractRequestUI(authState.recoverPassword),
);

export const getUpdatePasswordUI = createSelector(
  authStateSelector,
  authState => extractRequestUI(authState.updatePassword),
);

export const getIntegrationTokenUI = createSelector(
  authStateSelector,
  authState => extractRequestUI(authState.integrationToken),
);

// Dashboard completed
export const getDashboardCompletedPeriodTabs = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState => {
    const rangeProp =
      dashboardCompletedState.details.range ===
      dashboarCompletedDetailsRanges.WEEK
        ? 'week'
        : 'month';

    return dashboardCompletedState.periodList.list.reduce((tabs, item) => {
      const itemRangeId =
        rangeProp === 'week'
          ? moment(item.referenceDate).format('wwgggg')
          : moment(item.referenceDate).format('MMYYYY');
      const rangeTab = tabs.find(tab => tab.rangeId === itemRangeId);

      return rangeTab
        ? // if rangeTab is found, update only finalReferenceDate
          [
            ...tabs.slice(0, tabs.length - 1),
            {
              ...rangeTab,
              finalReferenceDate: item.referenceDate,
            },
          ]
        : // if rangeTab not found, push new rangeTab object
          [
            ...tabs,
            {
              rangeId: itemRangeId,
              initialReferenceDate: item.referenceDate,
              finalReferenceDate: item.referenceDate,
            },
          ];
    }, []);
  },
);

export const getDashboardCompletedComparative = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState => {
    const data = dashboardCompletedState.comparative.data;
    const range = dashboardCompletedState.details.range;
    const currentRangeId = dashboardCompletedState.details.currentRangeId;

    if (data && data[range] && data[range][currentRangeId]) {
      return data[range][currentRangeId];
    }
    return null;
  },
);

export const getDashboardCompletedComparativeBy = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState => {
    const {
      details: { range, currentRangeId, comparativeType },
      comparativeBy: { data },
    } = dashboardCompletedState;

    // const data = comparativeBy.data;
    // const range = details.range;
    // const currentRangeId = details.currentRangeId;
    // const currentComparativeType = details.comparativeType;

    if (
      data &&
      data[range] &&
      data[range][currentRangeId] &&
      data[range][currentRangeId][comparativeType]
    ) {
      return data[range][currentRangeId][comparativeType];
    }

    return null;
  },
);

export const getDashboardCompletedBigNumbersUI = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState =>
    extractRequestUI(dashboardCompletedState.bigNumbers),
);

export const getDashboardCompletedPeriodListUI = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState =>
    extractRequestUI(dashboardCompletedState.periodList),
);

export const getDashboardCompletedComparativeUI = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState =>
    extractRequestUI(dashboardCompletedState.comparative),
);

export const getDashboardCompletedComparativeByUI = createSelector(
  dashboarCompletedStateSelector,
  dashboardCompletedState =>
    extractRequestUI(dashboardCompletedState.comparativeBy),
);

// Admin Platform Users

/**
 * returns request states for request platform users
 * @return {Object} isFetching, requested, error
 */
export const getPlatformUsersUI = createSelector(
  platformUsersSelector,
  adminPlatformUsers => extractRequestUI(adminPlatformUsers.platformUsers),
);

/**
 * returns request states for add platform user
 * @return {Object} isFetching, requested, error
 */
export const getAddPlatformUserUI = createSelector(
  platformUsersSelector,
  adminPlatformUsers => extractRequestUI(adminPlatformUsers.addPlatformUser),
);

/**
 * returns request states for update platform user
 * @return {Object} isFetching, requested, error
 */
export const getUpdatePlatformUserUI = createSelector(
  platformUsersSelector,
  adminPlatformUsers => extractRequestUI(adminPlatformUsers.updatePlatformUser),
);

/**
 * returns request states for batch update platform user
 * @return {Object} isFetching, requested, error
 */
export const getBatchUpdatePlatformUsersUI = createSelector(
  platformUsersSelector,
  adminPlatformUsers =>
    extractRequestUI(adminPlatformUsers.batchUpdatePlatformUsers),
);

/**
 * returns request states for remove platform user
 * @return {Object} isFetching, requested, error
 */
export const getRemovePlatformUserUI = createSelector(
  platformUsersSelector,
  adminPlatformUsers => extractRequestUI(adminPlatformUsers.removePlatformUser),
);

// Admin Companies

/**
 * returns request states for request companies
 * @return {Object} isFetching, requested, error
 */
export const getCompaniesUI = createSelector(
  companiesSelector,
  adminCompanies => extractRequestUI(adminCompanies.companies),
);

/**
 * returns request states for add company
 * @return {Object} isFetching, requested, error
 */
export const getAddCompanyUI = createSelector(
  companiesSelector,
  adminCompanies => extractRequestUI(adminCompanies.addCompany),
);

/**
 * returns request states for update company
 * @return {Object} isFetching, requested, error
 */
export const getUpdateCompanyUI = createSelector(
  companiesSelector,
  adminCompanies => extractRequestUI(adminCompanies.updateCompany),
);

/**
 * returns request states for remove company
 * @return {Object} isFetching, requested, error
 */
export const getRemoveCompanyUI = createSelector(
  companiesSelector,
  adminCompanies => extractRequestUI(adminCompanies.removeCompany),
);

// Admin Luft Users

/**
 * returns request states for request luft users
 * @return {Object} isFetching, requested, error
 */
export const getLuftUsersUI = createSelector(
  luftUsersSelector,
  adminLuftUsers => extractRequestUI(adminLuftUsers.luftUsers),
);

/**
 * returns request states for add luft user
 * @return {Object} isFetching, requested, error
 */
export const getAddLuftUserUI = createSelector(
  luftUsersSelector,
  adminLuftUsers => extractRequestUI(adminLuftUsers.addLuftUser),
);

/**
 * returns request states for update luft user
 * @return {Object} isFetching, requested, error
 */
export const getUpdateLuftUserUI = createSelector(
  luftUsersSelector,
  adminLuftUsers => extractRequestUI(adminLuftUsers.updateLuftUser),
);

/**
 * returns request states for remove luft user
 * @return {Object} isFetching, requested, error
 */
export const getRemoveLuftUserUI = createSelector(
  luftUsersSelector,
  adminLuftUsers => extractRequestUI(adminLuftUsers.removeLuftUser),
);

// =========================
// Dashboard Home
// =========================
export const getDashboardHomeBigNumbersUI = createSelector(
  dashboardHomeStateSelector,
  dashboardHomeState => extractRequestUI(dashboardHomeState.bigNumbers),
);

export const getDashboardHomeProjectedFulfilledUI = createSelector(
  dashboardHomeStateSelector,
  dashboardHomeState => extractRequestUI(dashboardHomeState.projectedFulfilled),
);

// =========================
// Dashboard Home > Delivery Status Prime
// =========================
export const getDashboardHomePrimeDeliveryStatusUI = createSelector(
  dashboardHomeStateSelector,
  dashboardHomeState =>
    extractRequestUI(dashboardHomeState.primeDeliveryStatus),
);
export const getDashboardHomePrimeDeliveryOnTimeDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.primeDeliveryStatus.data.ontime),
  );
export const getDashboardHomePrimeDeliveryLateRiskDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.primeDeliveryStatus.data.lateRisk),
  );
export const getDashboardHomePrimeDeliveryLateDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.primeDeliveryStatus.data.late),
  );

// =========================
// Dashboard Home > Delivery Status Normal
// =========================
export const getDashboardHomeNormalDeliveryOnTimeDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.normalDeliveryStatus.data.ontime),
  );
export const getDashboardHomeNormalDeliveryLateRiskDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.normalDeliveryStatus.data.lateRisk),
  );
export const getDashboardHomeNormalDeliveryLateDeliveriesStatusUI =
  createSelector(dashboardHomeStateSelector, dashboardHomeState =>
    extractRequestUI(dashboardHomeState.normalDeliveryStatus.data.late),
  );
export const getDashboardHomeNormalDeliveryStatusUI = createSelector(
  dashboardHomeStateSelector,
  dashboardHomeState =>
    extractRequestUI(dashboardHomeState.normalDeliveryStatus),
);

// Delivery Tracking
export const getNFListUI = createSelector(
  trackingSelector,
  deliveryTrackingState => extractRequestUI(deliveryTrackingState.nfList),
);

export const getTrackingExportDetailedExcelUI = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.exportDetailedExcel),
);

export const getTrackingExportExcelUI = createSelector(
  trackingSelector,
  deliveryTrackingState => extractRequestUI(deliveryTrackingState.exportExcel),
);

export const getExportStockUI = createSelector(stockStateSelector, stockState =>
  extractRequestUI(stockState.exportStock),
);

export const getActiveNfForecastHistoryUI = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.activeNfForecastHistory),
);

export const getActiveNFListStatusUI = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.activeNfListStatus),
);

export const getCurrentNFDetails = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    deliveryTrackingState.nfList.data.list.find(
      el => el.id === deliveryTrackingState.currentNFDetailsId,
    ),
);

export const getAddNFEmailTrackingUI = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.addNFEmailTracking),
);

export const getRemoveNFEmailTrackingUI = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.removeNFEmailTracking),
);

export const getActiveNfOccurrencesUi = createSelector(
  trackingSelector,
  deliveryTrackingState =>
    extractRequestUI(deliveryTrackingState.activeNfOccurrences),
);

// Suggestion
export const getSendSuggestionUI = createSelector(
  suggestionSelector,
  suggestionState => extractRequestUI(suggestionState.sendSuggestion),
);

// Dashboard Forecast
export const getDashboardForecastUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState => extractRequestUI(dashboardForecastState.forecast),
);

export const getDashboardForecastBigNumbers = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    dashboardForecastState.forecast.data.bigNumbers[
      getMeasurementUnitFieldName(dashboardForecastState.forecastViewUnit)
    ],
);

export const getCurrentDashboardForecastChartData = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState => {
    const { projectedFulfilledChartData } =
      dashboardForecastState.forecast.data;
    return projectedFulfilledChartData.map(el => {
      const currentViewUnitData =
        el[
          getMeasurementUnitFieldName(dashboardForecastState.forecastViewUnit)
        ];
      return {
        referenceDate: el.referenceDate,
        fullDate: el.fullDate,
        projected: currentViewUnitData.projected,
        fulfilled: currentViewUnitData.fulfilled,
        diffToProjected: currentViewUnitData.diffToProjected,
      };
    });
  },
);

// Dashboard Forecast Details
export const getDashboardForecastDetailsUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    extractRequestUI(dashboardForecastState.forecastDetails),
);

export const getCurrentDashboardForecastDetails = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState => {
    const { data } = dashboardForecastState.forecastDetails;
    return data.map(el => {
      const currentViewUnitFieldName = getMeasurementUnitFieldName(
        dashboardForecastState.forecastViewUnit,
      );
      return {
        groupEntityName: el.groupEntityName,
        impactPercentage: el.impactPercentage[currentViewUnitFieldName],
        completed: el.completed[currentViewUnitFieldName],
        chartData: el.chartData[currentViewUnitFieldName],
      };
    });
  },
);

// Dashboard Forecast Period
export const getAddDashboardForecastPeriodUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    extractRequestUI(dashboardForecastState.addForecastPeriod),
);

export const getUpdateDashboardForecastPeriodUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    extractRequestUI(dashboardForecastState.updateForecastPeriod),
);

export const getRemoveDashboardForecastPeriodUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    extractRequestUI(dashboardForecastState.removeForecastPeriod),
);

export const getDashboardForecastPeriodUI = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    extractRequestUI(dashboardForecastState.forecastPeriodList),
);

export const getCurrentForecastPeriod = createSelector(
  dashboardForecastStateSelector,
  dashboardForecastState =>
    dashboardForecastState.forecastPeriodList.data.find(
      period => period.id === dashboardForecastState.currentForecastPeriodId,
    ),
);

// =========================
// Dashboard RealTime
// =========================
export const getDashboardRealTimeUI = createSelector(
  dashboardRealTimeStateSelector,
  dashboardRealTime =>
    extractMultipleRequestUI([
      dashboardRealTime.onTime,
      dashboardRealTime.late,
      dashboardRealTime.lateRisk,
    ]),
);

export const getDashboardActiveNfUI = createSelector(
  dashboardRealTimeStateSelector,
  dashboardRealTime => extractRequestUI(dashboardRealTime.activeNf),
);

export const getDashboardRealTimeNfPeriodStats = (
  isKeyAcc,
  performanceStatusCode,
  storeKey,
) =>
  createSelector(deliveryStatusSelector, deliveryStatus => {
    const periodStats = deliveryStatus[storeKey].deliveries
      // Filter by keyAccount flag
      .filter(nfObj => {
        if (isKeyAcc === null) return nfObj;

        return nfObj.tms.deliveryImportant
          ? isKeyAcc
            ? nfObj
            : false
          : !isKeyAcc
          ? nfObj
          : false;
      })
      // Filter by performance status code
      .filter(nfObj => {
        if (
          Array.isArray(performanceStatusCode) ? 
          performanceStatusCode.some(item => item === nfObj.performance) :
          performanceStatusCode === nfObj.performance)
        {
          return nfObj;
        }

        return false;
      })
      // Count periods
      .reduce(
        (prevObj, nfObj, index, filteredArray) => {
          const nfListLength = filteredArray.length;

          // temporary period
          const periods = {
            DAWN: 0,
            MORNING: 1,
            AFTERNOON: 2,
            NIGHT: 3,
          };

          const shouldAdd = (prevValue, periodToMatch) =>
            prevValue + (nfObj.tms.periodDay === periodToMatch ? 1 : 0);

          const morningCountSum = shouldAdd(
            prevObj.morningCount,
            periods.MORNING,
          );
          const afternoonCountSum = shouldAdd(
            prevObj.afternoonCount,
            periods.AFTERNOON,
          );
          const nightCountSum = shouldAdd(prevObj.nightCount, periods.NIGHT);
          const dawnCountSum = shouldAdd(prevObj.dawnCount, periods.DAWN);

          const morningPercent = (
            (morningCountSum * 100) /
            nfListLength
          ).toFixed(2);
          const afternoonPercent = (
            (afternoonCountSum * 100) /
            nfListLength
          ).toFixed(2);
          const nightPercent = ((nightCountSum * 100) / nfListLength).toFixed(
            2,
          );
          const dawnPercent = ((dawnCountSum * 100) / nfListLength).toFixed(2);

          return {
            morningCount: morningCountSum,
            morningPercent,
            afternoonCount: afternoonCountSum,
            afternoonPercent,
            nightCount: nightCountSum,
            nightPercent,
            dawnCount: dawnCountSum,
            dawnPercent,
          };
        },
        {
          morningCount: 0,
          morningPercent: 0,
          afternoonCount: 0,
          afternoonPercent: 0,
          nightCount: 0,
          nightPercent: 0,
          dawnCount: 0,
          dawnPercent: 0,
        },
      );

    return periodStats;
  });

// =========================
// Delivery Status
// =========================
export const getDeliveryStatusDeliveriesByAccType = (
  isKeyAcc,
  performanceStatusCode,
  storeKey,
) =>
  createSelector(deliveryStatusSelector, deliveryStatus => {
    // Filter NF List based on function params
    // Filter by keyAccount flag
    const filteredNfList = deliveryStatus[storeKey].deliveries
      .filter(nfObj => {
        if (isKeyAcc === null) return nfObj;

        return nfObj.tms.deliveryImportant
          ? isKeyAcc
            ? nfObj
            : false
          : !isKeyAcc
          ? nfObj
          : false;
      }).map(nfObj => {
        if (nfObj.performance === 4) nfObj.performance = 1;
        if (nfObj.performance === 5) nfObj.performance = 2;
        if (nfObj.performance === 6) nfObj.performance = 3;
        if (nfObj.performance === 7) nfObj.performance = 3;

        return nfObj;
      })
      // Filter by performance status code
      .filter(nfObj => {
        if (
          Array.isArray(performanceStatusCode) ? 
          performanceStatusCode.some(item => item === nfObj.performance) :
          performanceStatusCode === nfObj.performance)
        {
          return nfObj;
        }

        return false;
      });
    return filteredNfList;
  });

export const getDeliveryStatusCounters = (isKeyAcc, storeKey) =>
  createSelector(deliveryStatusSelector, deliveryStatus => {
    const counter = deliveryStatus[storeKey].deliveries
      .filter(nfObj => {
        if (isKeyAcc === null && nfObj.performance !== 0) return nfObj;
        if (
          nfObj.tms.deliveryImportant === isKeyAcc &&
          nfObj.performance !== 0
        ) {
          return nfObj;
        }
        return false;
      })
      .reduce(
        (prevObj, nfObj, index, filteredArray) => {
          const nfPerfStatus = nfObj.performance;
          const nfListLength = filteredArray.length;

          const shouldAdd = (prevValue, performanceStatusToMatch) =>
            prevValue + (nfPerfStatus === performanceStatusToMatch ? 1 : 0);

          const lateRiskSum = shouldAdd(
            prevObj.lateRiskTotalCount,
            performanceStatus.LATE_RISK,
          );
          const lateSum = shouldAdd(
            prevObj.lateTotalCount,
            performanceStatus.LATE,
          );
          const ontimeSum = shouldAdd(
            prevObj.ontimeTotalCount,
            performanceStatus.ONTIME,
          );

          const lateRiskPercent = () =>
            parseFloat(((lateRiskSum * 100) / nfListLength).toFixed(1));
          const latePercent = () =>
            parseFloat(((lateSum * 100) / nfListLength).toFixed(1));
          const ontimePercent = () =>
            parseFloat(((ontimeSum * 100) / nfListLength).toFixed(1));

          return {
            lateRiskTotalCount: lateRiskSum,
            lateRiskTotalPercent: lateRiskPercent(),
            lateTotalCount: lateSum,
            lateTotalPercent: latePercent(),
            ontimeTotalCount: ontimeSum,
            ontimeTotalPercent: ontimePercent(),
          };
        },
        {
          lateRiskTotalCount: 0,
          lateRiskTotalPercent: 0,
          lateTotalCount: 0,
          lateTotalPercent: 0,
          ontimeTotalCount: 0,
          ontimeTotalPercent: 0,
        },
      );

    return counter;
  });

export const getDeliveryStatusUI = storeKey =>
  createSelector(deliveryStatusSelector, deliveryStatus =>
    extractRequestUI(deliveryStatus[storeKey]),
  );

// Stock

export const getStockCurrentList = createSelector(
  stockStateSelector,
  stockState => {
    if (!stockState.grouped) return stockState.stockCurrentList.data;

    return stockState.stockCurrentList.data.map(branch => ({
      ...branch,
      products: branch.products.reduce((groupedList, current) => {
        const indexOfProduct = groupedList.findIndex(
          product => product.cod === current.cod,
        );
        if (indexOfProduct === -1) {
          return [
            ...groupedList,
            {
              ...current,
              loteCount: 1,
            },
          ];
        }

        const matchedProduct = groupedList[indexOfProduct];

        return [
          ...groupedList.slice(0, indexOfProduct),
          {
            ...matchedProduct,
            pallets: matchedProduct.pallets + current.pallets,
            volume: matchedProduct.volume + current.volume,
            value: matchedProduct.value + current.value,
            weight: matchedProduct.weight + current.weight,
            quantity: matchedProduct.quantity + current.quantity,
            loteCount: matchedProduct.loteCount + 1,
          },
          ...groupedList.slice(indexOfProduct + 1),
        ];
      }, []),
    }));
  },
);

export const getStockCurrentUI = createSelector(stockStateSelector, stock =>
  extractRequestUI(stock.stockCurrentList),
);

export const getStockCurrentTotalizerUI = createSelector(
  stockStateSelector,
  stock => extractRequestUI(stock.stockCurrentTotalizer),
);

export const getStockMovementUI = createSelector(stockStateSelector, stock =>
  extractRequestUI(stock.stockMovementList),
);

export const getDailyMovimentationUI = createSelector(
  stockStateSelector,
  stock => extractRequestUI(stock.dailyMovimentation),
);

export const getProductMovimentationUI = createSelector(
  stockStateSelector,
  stock => extractRequestUI(stock.productMovimentation),
);

export const getNfMovimentationReceivedUI = createSelector(
  stockStateSelector,
  stock => extractRequestUI(stock.received),
);

export const getNfMovimentationIssuedUI = createSelector(
  stockStateSelector,
  stock => extractRequestUI(stock.issued),
);
// ==========================
// Scheduling
// ==========================
export const getScheduleListUI = createSelector(
  schedulingSelector,
  scheduling => extractRequestUI(scheduling.scheduleList),
);

export const getScheduleNFListUI = createSelector(
  schedulingSelector,
  scheduling => extractRequestUI(scheduling.activeScheduleNFList),
);

export const getDeleteScheduleUI = createSelector(
  schedulingSelector,
  scheduling => extractRequestUI(scheduling.deleteSchedule),
);

export const getLoadNFListUI = createSelector(
  newScheduleSelector,
  newSchedule => extractRequestUI(newSchedule.loadSchedule.nfList),
);

export const getDriversUI = createSelector(newScheduleSelector, newSchedule =>
  extractRequestUI(newSchedule.drivers),
);

export const getVehiclesUI = createSelector(newScheduleSelector, newSchedule =>
  extractRequestUI(newSchedule.vehicles),
);

export const getAvailableHoursUI = createSelector(
  newScheduleSelector,
  newSchedule => extractRequestUI(newSchedule.availableHours),
);

export const getAddScheduleUI = createSelector(
  newScheduleSelector,
  newSchedule => extractRequestUI(newSchedule.addSchedule),
);

export const getScheduleData = createSelector(
  newScheduleSelector,
  newSchedule => {
    if (newSchedule.currentType === 0) {
      return {
        scheduling: {
          startDate: newSchedule.loadSchedule.dateTimeFrom,
          endDate: newSchedule.loadSchedule.dateTimeTo,
          type: newSchedule.currentType,
          driverId: newSchedule.loadSchedule.selectedDriver.id,
          vehiclesId: newSchedule.loadSchedule.selectedVehicle.id,
          dock: 7,
          warehouseId: 7,
          unloadType: 1,
        },
        nfList: newSchedule.loadSchedule.nfSelectedsList.map(obj => ({
          invoiceId: obj.invoiceId,
          nf: obj.nf,
          nfGroup: obj.nfGroup,
          nfDate: obj.nfDate,
          receiver: obj.receiver,
          cidade: obj.cidade,
          uf: obj.uf,
          type: obj.type,
          isDamaged: obj.isDamaged,
          isPalletized: obj.isPalletized,
          volume: obj.volume,
          weight: obj.weight,
          value: obj.value,
          status: obj.status,
        })),
      };
    }
    return {
      scheduling: {
        startDate: newSchedule.unloadSchedule.dateTimeFrom,
        endDate: newSchedule.unloadSchedule.dateTimeTo,
        type: newSchedule.currentType,
        driverId: newSchedule.unloadSchedule.selectedDriver.id,
        vehiclesId: newSchedule.unloadSchedule.selectedVehicle.id,
        dock: 7,
        warehouseId: 7,
        unloadType: newSchedule.unloadSchedule.unloadType,
      },
      nfList: newSchedule.unloadSchedule.nfList.map(obj => ({
        invoiceId: 0,
        nfGroup: obj.nfGroup,
        nf: obj.nf,
        nfDate: moment().format('YYYY-MM-DD'),
        receiver: obj.receiver,
        cidade: obj.cidade,
        uf: obj.uf,
        type: obj.type,
        isDamaged: obj.isDamaged,
        isPalletized: obj.isPalletized,
        volume: obj.volume,
        weight: obj.weight,
        value: obj.value,
        status: 0,
      })),
    };
  },
);

export const getPackagingTypesUI = createSelector(
  newScheduleSelector,
  newSchedule => extractRequestUI(newSchedule.packagingTypes),
);

// ==========================
// Admin notification settings
// ==========================
export const getNotificationSubsUI = createSelector(
  notificationSettingsSelector,
  notificationSettings =>
    extractRequestUI(notificationSettings.notificationSubs),
);

export const getUpdateNotificationSubUi = createSelector(
  notificationSettingsSelector,
  notificationSettings =>
    extractRequestUI(notificationSettings.updateNotificationSub),
);

export const getAddNotificationSubUi = createSelector(
  notificationSettingsSelector,
  notificationSettings =>
    extractRequestUI(notificationSettings.addNotificationSub),
);

export const getDeleteNotificationSubUi = createSelector(
  notificationSettingsSelector,
  notificationSettings =>
    extractRequestUI(notificationSettings.deleteNotificationSub),
);
